import { FC, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ToRentPriceMinSelectOptions,
    ToRentPriceMaxSelectOptions
} from '@zoopla/listing-search/filters/options';
import { DropdownWithLabel } from '@blueprint/dropdown';
import { MultiColumnDropdownWithLabel } from '@blueprint/multicolumn-dropdown';
import {
    updateFilter,
    resetFilters,
    minPriceWeeklySelector,
    maxPriceWeeklySelector,
    minPriceMonthlySelector,
    maxPriceMonthlySelector,
    priceFrequencySelector
} from '@zoopla/redux-reducer-filters';
import { DropdownContainer } from '../DropdownContainer';
import { buildSelectPriceTitleToRent } from '@zoopla/redux-reducer-filters';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

const filtersFieldPriceLabel = 'Any price';
const minPriceLabel = 'Minimum price';
const maxPriceLabel = 'Maximum price';

const AllDropdownsWrapper = styled.div(
    () => css`
        min-width: 28.525rem;
    `
);

const StyledDropdownToRentPrice = styled(DropdownWithLabel)(
    () => css`
        min-width: 15rem;
    `
);

const DropdownWrapper = styled.div(
    () => css`
        display: flex;
    `
);

export const SelectPriceToRent: FC = () => {
    const reduxDispatch = useDispatch();
    const {
        selectedValue: priceFrequencySelectedValue,
        options: priceFrequencyOptions
    } = useSelector(priceFrequencySelector);

    const {
        selectedValue: priceMinWeeklySelectedValue,
        options: priceMinWeeklyOptions
    } = useSelector(minPriceWeeklySelector);

    const {
        selectedValue: priceMaxWeeklySelectedValue,
        options: priceMaxWeeklyOptions
    } = useSelector(maxPriceWeeklySelector);

    const {
        selectedValue: priceMinMonthlySelectedValue,
        options: priceMinMonthlyOptions
    } = useSelector(minPriceMonthlySelector);

    const {
        selectedValue: priceMaxMonthlySelectedValue,
        options: priceMaxMonthlyOptions
    } = useSelector(maxPriceMonthlySelector);

    const priceButtonTextToRent = buildSelectPriceTitleToRent(
        priceMinWeeklySelectedValue,
        priceMaxWeeklySelectedValue,
        priceMinMonthlySelectedValue,
        priceMaxMonthlySelectedValue,
        priceFrequencySelectedValue
    );

    const onPriceFrequencySelect = (evt: ChangeEvent<HTMLSelectElement>) => {
        reduxDispatch(updateFilter('priceFrequency', evt.currentTarget.value));
        if (evt.currentTarget.value === 'per_month') {
            reduxDispatch(resetFilters(['maxPriceWeekly', 'minPriceWeekly']));
        } else {
            reduxDispatch(resetFilters(['maxPriceMonthly', 'minPriceMonthly']));
        }
    };

    const onMaxMonthlyPriceChange = (evt: ChangeEvent<HTMLSelectElement>) => {
        reduxDispatch(updateFilter('maxPriceMonthly', evt.currentTarget.value));
    };

    const onMinMonthlyPriceChange = (evt: ChangeEvent<HTMLSelectElement>) => {
        reduxDispatch(updateFilter('minPriceMonthly', evt.currentTarget.value));
    };

    const onMaxWeeklyPriceChange = (evt: ChangeEvent<HTMLSelectElement>) => {
        reduxDispatch(updateFilter('maxPriceWeekly', evt.currentTarget.value));
    };

    const onMinWeeklyPriceChange = (evt: ChangeEvent<HTMLSelectElement>) => {
        reduxDispatch(updateFilter('minPriceWeekly', evt.currentTarget.value));
    };

    const priceMinMonthlyInt = parseInt(
        priceMinMonthlySelectedValue || '0',
        10
    );
    const priceMaxMonthlyInt = parseInt(
        priceMaxMonthlySelectedValue || '0',
        10
    );
    const priceMinWeeklyInt = parseInt(priceMinWeeklySelectedValue || '0', 10);
    const priceMaxWeeklyInt = parseInt(priceMaxWeeklySelectedValue || '0', 10);

    return (
        <MultiColumnDropdownWithLabel
            title={priceButtonTextToRent}
            label="Price range"
            testId="any_price"
            isLeft
        >
            <AllDropdownsWrapper>
                <DropdownContainer>
                    <DropdownWithLabel
                        id="desktop_price_frequency-filter"
                        data-testid="desktop_price_frequency-filter-select"
                        onChange={onPriceFrequencySelect}
                        value={priceFrequencySelectedValue}
                        name="priceFrequency"
                        hasBorderRadius
                        aria-label="Price Frequency"
                        label="Price per"
                    >
                        {' '}
                        {priceFrequencyOptions ? (
                            priceFrequencyOptions?.map((option) => (
                                <option
                                    key={option?.value}
                                    value={option?.value}
                                >
                                    {option.label}
                                </option>
                            ))
                        ) : (
                            <option></option>
                        )}
                    </DropdownWithLabel>
                </DropdownContainer>
                {priceFrequencySelectedValue !== 'per_week' && (
                    <DropdownWrapper>
                        <DropdownContainer>
                            <StyledDropdownToRentPrice
                                onChange={onMinMonthlyPriceChange}
                                value={priceMinMonthlySelectedValue}
                                name="price_min_monthly"
                                id="price_min_monthly"
                                hasBorderRadius
                                aria-label="Min Price monthly"
                                data-testid="min_price"
                                label={minPriceLabel}
                            >
                                {priceMinMonthlyOptions ? (
                                    <ToRentPriceMinSelectOptions
                                        options={priceMinMonthlyOptions}
                                        priceOtherInt={priceMaxMonthlyInt}
                                        keyPrefix="minmonth"
                                    />
                                ) : (
                                    <option>{filtersFieldPriceLabel}</option>
                                )}
                            </StyledDropdownToRentPrice>
                        </DropdownContainer>
                        <DropdownContainer>
                            <StyledDropdownToRentPrice
                                onChange={onMaxMonthlyPriceChange}
                                value={priceMaxMonthlySelectedValue}
                                name="price_max_monthly"
                                id="price_max_monthly"
                                hasBorderRadius
                                aria-label="Max Price monthly"
                                data-testid="max_price"
                                label={maxPriceLabel}
                            >
                                {priceMaxMonthlyOptions ? (
                                    <ToRentPriceMaxSelectOptions
                                        options={priceMaxMonthlyOptions}
                                        keyPrefix="maxmonth"
                                        priceOtherInt={priceMinMonthlyInt}
                                    />
                                ) : (
                                    <option>{filtersFieldPriceLabel}</option>
                                )}
                            </StyledDropdownToRentPrice>
                        </DropdownContainer>
                    </DropdownWrapper>
                )}
                {priceFrequencySelectedValue === 'per_week' && (
                    <DropdownWrapper>
                        <DropdownContainer>
                            <StyledDropdownToRentPrice
                                onChange={onMinWeeklyPriceChange}
                                value={priceMinWeeklySelectedValue}
                                name="price_min_weekly"
                                id="price_min_weekly"
                                hasBorderRadius
                                aria-label="Min Price weekly"
                                data-testid="min_price"
                                label={minPriceLabel}
                            >
                                {priceMinWeeklyOptions ? (
                                    <ToRentPriceMinSelectOptions
                                        options={priceMinWeeklyOptions}
                                        priceOtherInt={priceMaxWeeklyInt}
                                        keyPrefix="minweek"
                                    />
                                ) : (
                                    <option>{filtersFieldPriceLabel}</option>
                                )}
                            </StyledDropdownToRentPrice>
                        </DropdownContainer>
                        <DropdownContainer>
                            <StyledDropdownToRentPrice
                                onChange={onMaxWeeklyPriceChange}
                                value={priceMaxWeeklySelectedValue}
                                name="price_max_weekly"
                                id="price_max_weekly"
                                hasBorderRadius
                                aria-label="Max Price weekly"
                                data-testid="max_price"
                                label={maxPriceLabel}
                            >
                                {priceMaxWeeklyOptions ? (
                                    <ToRentPriceMaxSelectOptions
                                        options={priceMaxWeeklyOptions}
                                        keyPrefix="maxweek"
                                        priceOtherInt={priceMinWeeklyInt}
                                    />
                                ) : (
                                    <option>{filtersFieldPriceLabel}</option>
                                )}
                            </StyledDropdownToRentPrice>
                        </DropdownContainer>
                    </DropdownWrapper>
                )}
            </AllDropdownsWrapper>
        </MultiColumnDropdownWithLabel>
    );
};
